import { useState, type CSSProperties, type ReactElement } from 'react';

import { cn } from '@/components/ui/cn';

import type { PreviewStyle } from '../watermark-form';
import type { Purpose } from '../types';

interface Props {
  /** Texto de la leyenda de cada propósito, tal como está en el borrador. */
  legends: Record<Purpose, string>;
  style: PreviewStyle;
  /** Línea de trazabilidad de ejemplo (la estampa el sistema en cada copia). */
  traceLine: string;
}

const TILES = 9;
/** Ancho de una página A4 en puntos: el tamaño en pt se escala al ancho de la vista. */
const A4_WIDTH_PT = 595;

/** Vista previa simulada en el navegador (no pide el PDF: eso registraría una copia).
 *  Replica la marca del motor: leyenda + línea de trazabilidad, rotación, color,
 *  opacidad y mosaico, sobre una página de muestra. */
export function WatermarkPreview({ legends, style, traceLine }: Props): ReactElement {
  const [purpose, setPurpose] = useState<Purpose>('interna');
  const legend = legends[purpose];
  const layer: CSSProperties = {
    opacity: style.opacity,
    color: style.colorHex,
    // pdf-lib rota en sentido antihorario; CSS en sentido horario.
    transform: `rotate(${-style.rotationDeg}deg)`,
  };
  const size = (pt: number): string => `${((pt / A4_WIDTH_PT) * 100).toFixed(3)}cqw`;
  const stamp = (key: number): ReactElement => (
    <span key={key} className="wmstamp">
      <span style={{ fontSize: size(style.fontSizePt) }}>{legend}</span>
      <small style={{ fontSize: size(Math.max(8, Math.round(style.fontSizePt * 0.35))) }}>
        {traceLine}
      </small>
    </span>
  );

  return (
    <div>
      <div className="rowb wmhead">
        <span className="eyebrow">Vista previa</span>
        <div className="seg" role="group" aria-label="Tipo de copia de la vista previa">
          {(['interna', 'externa'] as const).map((p) => (
            <button
              key={p}
              type="button"
              className={cn(purpose === p && 'on')}
              aria-pressed={purpose === p}
              onClick={() => setPurpose(p)}
            >
              {p === 'interna' ? 'Interna' : 'Externa'}
            </button>
          ))}
        </div>
      </div>
      <figure className="wmdoc" aria-label="Vista previa de la marca de agua">
        <div className={cn('wmlayer', !style.tiled && 'single')} style={layer}>
          {style.tiled ? Array.from({ length: TILES }, (_, i) => stamp(i)) : stamp(0)}
        </div>
        <div className="wmpage" aria-hidden="true">
          <div className="wmline" style={{ width: '46%', height: 12 }} />
          <div className="wmline" style={{ width: '30%', marginTop: 8 }} />
          {Array.from({ length: 9 }, (_, i) => (
            <div
              key={i}
              className="wmline"
              style={{ width: `${88 - (i % 3) * 9}%`, marginTop: 14 }}
            />
          ))}
        </div>
        <figcaption className="soft">
          Vista previa con la configuración en edición. La copia lleva la marca en cada página.
        </figcaption>
      </figure>
    </div>
  );
}
